import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import _get from 'lodash/get';
import { RootState } from '../../configuration';
import {
  ArenaChallengeDetail,
  createTeam,
  joinTeam,
  kickMember,
  leaveTeam,
  getChallenge,
  submitEntry,
} from '../../api/group/arena.api';
import ArenaTeamPanel from './components/arena-team-panel';
import ArenaLeaderboard from './components/arena-leaderboard';
import { STATUS_LABEL, formatVn } from './arena-format';

const BTN = 'rounded-full px-4 py-2 text-[14px] font-medium transition disabled:opacity-60';
const INPUT =
  'min-w-0 flex-1 rounded-full border border-[#DEE0E6] px-4 py-2 text-[14px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

const ArenaDetailPage: React.FC = () => {
  const { challengeId } = useParams<{ challengeId: string }>();
  const profile = useSelector((s: RootState) => s.features.auth.profile);
  const myUserId = _get(profile, 'userId', '') as string;

  const [detail, setDetail] = useState<ArenaChallengeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [naming, setNaming] = useState(false);
  const [code, setCode] = useState('');

  const load = async () => {
    if (!challengeId) return;
    try {
      setDetail(await getChallenge(challengeId));
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được thử thách');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId]);

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Không thực hiện được');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spin />
      </div>
    );
  }
  if (!detail) {
    return <div className="p-10 text-center text-[#657384] dark:text-slate-400">Thử thách không tồn tại.</div>;
  }

  const { challenge: c, myTeam, leaderboard, clanLeaderboard } = detail;
  const statusLabel = STATUS_LABEL[c.status] || c.status;
  const isClanWar = c.type === 'CLAN_WAR';

  return (
    <div className="mx-auto flex w-full max-w-[900px] flex-col gap-6 px-6 py-6">
      <Link to="/arena" className="text-[13px] text-[#066bff]">
        Quay lại danh sách
      </Link>

      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[24px] font-bold text-[#19052f] dark:text-slate-100">{c.title}</h1>
          <span className="rounded-full bg-[#DBEAFE] px-3 py-1 text-[12px] font-medium text-[#1D4ED8]">{statusLabel}</span>
          {isClanWar && (
            <span className="rounded-full bg-[#FEE2E2] px-3 py-1 text-[12px] font-medium text-[#B91C1C]">Clan chiến</span>
          )}
        </div>
        {c.description && <p className="whitespace-pre-line text-[14px] text-[#475569] dark:text-slate-300">{c.description}</p>}
      </header>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          ['Bắt đầu', c.startAt],
          ['Hạn nộp bài', c.submitDeadline],
          ['Hạn chấm', c.judgingDeadline],
        ].map(([label, v]) => (
          <div key={label} className="rounded-[12px] border border-[#DEE0E6] p-3 dark:border-slate-700">
            <div className="text-[12px] text-[#657384] dark:text-slate-400">{label}</div>
            <div className="text-[14px] font-medium text-[#19052f] dark:text-slate-100">{formatVn(v)}</div>
          </div>
        ))}
      </section>

      {isClanWar && (
        <div className="rounded-[12px] bg-[#FEF2F2] px-4 py-3 text-[14px] text-[#B91C1C] dark:bg-red-900/30 dark:text-red-300">
          Chỉ thành viên cùng clan, mỗi clan tối đa 3 đội.
        </div>
      )}

      {c.rules && (
        <section className="flex flex-col gap-2">
          <h2 className="text-[18px] font-semibold text-[#19052f] dark:text-slate-100">Luật thi</h2>
          <p className="whitespace-pre-line text-[14px] text-[#475569] dark:text-slate-300">{c.rules}</p>
          <p className="text-[13px] text-[#657384] dark:text-slate-400">
            Mỗi đội từ {c.teamSizeMin} đến {c.teamSizeMax} người.
          </p>
        </section>
      )}

      <section className="flex flex-col gap-1 text-[14px] text-[#475569] dark:text-slate-300">
        <h2 className="text-[18px] font-semibold text-[#19052f] dark:text-slate-100">Bảng điểm theo hạng</h2>
        <div>Hạng 1: {c.pointsRank1} điểm</div>
        <div>Hạng 2: {c.pointsRank2} điểm</div>
        <div>Hạng 3: {c.pointsRank3} điểm</div>
        <div>Hạng 4 đến 10: {c.pointsRank4To10} điểm</div>
        <div>Tham gia nộp bài: {c.pointsParticipation} điểm</div>
      </section>

      {c.status === 'JUDGING' && (
        <div className="rounded-[12px] bg-[#FEF3C7] px-4 py-3 text-[14px] text-[#B45309] dark:bg-amber-900/30 dark:text-amber-300">
          Đang chấm. Kết quả sẽ có sau khi chốt.
        </div>
      )}

      {myTeam ? (
        <ArenaTeamPanel
          challenge={c}
          team={myTeam}
          myUserId={myUserId}
          busy={busy}
          onLeave={() => run(() => leaveTeam(myTeam.id), 'Đã rời đội')}
          onKick={(uid) => run(() => kickMember(myTeam.id, uid), 'Đã mời thành viên ra khỏi đội')}
          onSubmit={(postId) => run(() => submitEntry(myTeam.id, postId), 'Đã nộp bài dự thi')}
        />
      ) : (
        c.isOpen && (
          <section className="flex flex-col gap-4 rounded-[16px] border border-[#DEE0E6] p-5 dark:border-slate-700">
            <h2 className="text-[18px] font-semibold text-[#19052f] dark:text-slate-100">Tham gia thử thách</h2>
            {naming ? (
              <div className="flex gap-2">
                <input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Tên đội"
                  maxLength={60}
                  className={INPUT}
                />
                <button
                  type="button"
                  disabled={busy || !teamName.trim()}
                  className={`${BTN} bg-[#066bff] text-white`}
                  onClick={() => run(() => createTeam(c.id, teamName.trim()), 'Đã lập đội')}
                >
                  Tạo đội
                </button>
              </div>
            ) : (
              <div>
                <button type="button" className={`${BTN} bg-[#066bff] text-white`} onClick={() => setNaming(true)}>
                  Lập đội
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Nhập mã mời" className={INPUT} />
              <button
                type="button"
                disabled={busy || !code.trim()}
                className={`${BTN} border border-[#066bff] text-[#066bff]`}
                onClick={() => run(() => joinTeam(c.id, code.trim()), 'Đã vào đội')}
              >
                Vào đội
              </button>
            </div>
          </section>
        )
      )}

      {c.status === 'FINALIZED' && clanLeaderboard && clanLeaderboard.length > 0 && (
        <section className="flex flex-col gap-3" data-testid="arena-clan-leaderboard">
          <h2 className="text-[18px] font-semibold text-[#19052f] dark:text-slate-100">Bảng xếp hạng clan</h2>
          <div className="overflow-x-auto rounded-[16px] border border-[#DEE0E6] dark:border-slate-700">
            <table className="w-full text-left text-[14px] text-[#19052f] dark:text-slate-100">
              <thead className="text-[13px] text-[#657384] dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2">Hạng</th>
                  <th className="px-3 py-2">Clan</th>
                  <th className="px-3 py-2 text-right">Tổng điểm bài</th>
                  <th className="px-3 py-2 text-right">Điểm nhận</th>
                  <th className="px-3 py-2 text-right">Số đội</th>
                </tr>
              </thead>
              <tbody>
                {clanLeaderboard.map((r) => (
                  <tr key={r.clanId} className="border-t border-[#F1F5F9] dark:border-slate-700">
                    <td className="px-3 py-2">{r.rank ?? '-'}</td>
                    <td className="px-3 py-2 font-medium">{r.clanName}</td>
                    <td className="px-3 py-2 text-right">{Number(r.totalFinalScore).toLocaleString('vi-VN')}</td>
                    <td className="px-3 py-2 text-right">{Number(r.points).toLocaleString('vi-VN')}</td>
                    <td className="px-3 py-2 text-right">{r.teamCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {c.status === 'FINALIZED' && leaderboard && (
        <section className="flex flex-col gap-3">
          <h2 className="text-[18px] font-semibold text-[#19052f] dark:text-slate-100">Bảng xếp hạng đội</h2>
          {isClanWar && (
            <p className="text-[13px] text-[#657384] dark:text-slate-400">
              Điểm Clan chiến được tính cho clan, xem bảng xếp hạng clan ở trên.
            </p>
          )}
          <ArenaLeaderboard rows={leaderboard} myTeamId={myTeam?.id} hidePoints={isClanWar} />
        </section>
      )}
    </div>
  );
};

export default ArenaDetailPage;
